import {gsap} from 'gsap';
import {ScrollTrigger} from 'gsap/ScrollTrigger';
import Lottie from 'lottie-web';
gsap.registerPlugin(ScrollTrigger);

const {registerModule} = window;

const DEFAULT_ANIMATION_DURATION = 20000;
const DEFAULT_ANIMATION_SCRUB = 2;
const EMBEDDED_SCROLL_SCRUB = 1;
const EMBEDDED_SCROLL_START = 'top bottom';
const EMBEDDED_ANIMATION_DURATION = 1000;

// On-view mode defaults
const DEFAULT_VIEW_THRESHOLD = 0.5; // 50% visibility

class Animation {
  constructor(element) {
    this.element = element;
    this.animation = null;
    this.initialize();
  }

  initialize() {
    this.bindUi();
    this.setVars();
    this._setupByTriggerMode();
  }

  /**
   * Handle animation load failure.
   * Logs error for monitoring - visual gap remains visible to help content editors catch issues.
   * @param {string} mode - The trigger mode that failed
   */
  _handleAnimationError(mode) {
    if (typeof window !== 'undefined' && window.DD_LOGS) {
      window.DD_LOGS.logger.warn('Animation failed to load', {
        mode: mode,
        file: this.animationFile,
      });
    }
  }

  /**
   * Determine the trigger mode from data attributes or element type.
   * - 'scroll-pinned': Default for standalone <section> elements (original behavior)
   * - 'scroll': Viewport-based scroll animation (no pinning)
   * - 'hover': Play on hover, stop and reset on mouse leave
   * @return {string} The trigger mode
   */
  _getTriggerMode() {
    const dataMode = this.element.getAttribute('data-trigger-mode');
    if (dataMode) {
      return dataMode;
    }
    // Default to scroll-pinned for section elements (backwards compatibility)
    return 'scroll-pinned';
  }

  /**
   * Route to the appropriate setup method based on trigger mode.
   */
  _setupByTriggerMode() {
    const mode = this._getTriggerMode();

    if (mode === 'hover') {
      this._setupHoverAnimation();
    } else if (mode === 'scroll') {
      this._setupViewportScrollAnimation();
    } else if (mode === 'on-view') {
      this._setupOnViewAnimation();
    } else {
      // scroll-pinned (original behavior)
      this._checkMediaQueryAndSetup();
    }
  }

  /**
   * Original media query checking for standalone pinned scroll animations.
   */
  _checkMediaQueryAndSetup() {
    this.matchMedia = gsap.matchMedia();

    this.matchMedia.add({
      isMobile: '(max-width: 600px)',
      isTablet: '(max-width: 1023px) and (min-width: 601px)',
      isDesktop: '(min-width: 1024px)',
      isPortrait: '(orientation: portrait)',
      isLandscape: '(orientation: landscape)',
    }, (context) => {
      let displayElement = true;
      if (this.displayConditions.displayOn && !this.displayConditions.mobile && context.conditions.isMobile) {
        displayElement = false;
      }
      if (this.displayConditions.displayOn && !this.displayConditions.tablet && context.conditions.isTablet) {
        displayElement = false;
      }
      if (this.displayConditions.displayOn && !this.displayConditions.desktop && context.conditions.isDesktop) {
        displayElement = false;
      }
      if (this.displayConditions.displayOn && !this.displayConditions.portrait && context.conditions.isPortrait) {
        displayElement = false;
      }
      if (this.displayConditions.displayOn && !this.displayConditions.landscape && context.conditions.isLandscape) {
        displayElement = false;
      }
      if (displayElement) {
        this._setupPinnedScrollAnimation();
      }
    });
  }

  /**
   * Original pinned scroll animation setup (for standalone sections).
   */
  _setupPinnedScrollAnimation() {
    const playhead = {frame: 0};
    const target = this.ui.$animationContainer;
    const scrollHint = document.querySelector('.scrollHint');
    const st = {
      trigger: target,
      pin: true,
      start: 'top top',
      end: `+=${this.animationDuration}`,
      scrub: this.animationScrub,
      onUpdate: (self) => {
        if (scrollHint && !scrollHint.classList.contains('scrollHint__hidden') && self.progress * 100 > this.showScrollHintFor) {
          scrollHint.classList.add('scrollHint__hidden');
        }
      },
      onRefresh: () => {
        if (!this.jumpTarget || !this.unresolvedJump || this.jumpTarget.getBoundingClientRect().top <= 0) {
          this.unresolvedJump = false;
        } else {
          this.jumpTarget.scrollIntoView({behavior: 'instant'});
        }
      },
    };
    const ctx = gsap.context && gsap.context();
    this.animation = Lottie.loadAnimation({
      container: target,
      renderer: 'svg',
      loop: false,
      autoplay: false,
      path: this.animationFile,
      rendererSettings: {preserveAspectRatio: 'xMidYMid slice'},
    });
    this.animation.addEventListener('DOMLoaded', () => {
      const createTween = () => {
        this.animation.frameTween = gsap.to(playhead, {
          frame: this.animation.totalFrames - 1,
          ease: 'none',
          onUpdate: () => this.animation.goToAndStop(playhead.frame, true),
          scrollTrigger: st,
        });
        return () => this.animation.destroy && this.animation.destroy();
      };
      ctx && ctx.add ? ctx.add(createTween) : createTween();
      ScrollTrigger.sort();
      ScrollTrigger.refresh();
    });
  }

  /**
   * Scroll animation (no pinning).
   * Animation progresses over a set number of pixels of scroll.
   */
  _setupViewportScrollAnimation() {
    const playhead = {frame: 0};
    const target = this.ui.$animationContainer;
    const scrubValue = parseFloat(this.element.getAttribute('data-animation-scrub')) || EMBEDDED_SCROLL_SCRUB;
    const durationValue = parseFloat(this.element.getAttribute('data-animation-duration')) || EMBEDDED_ANIMATION_DURATION;

    const endValue = `+=${durationValue}`;

    this.animation = Lottie.loadAnimation({
      container: target,
      renderer: 'svg',
      loop: false,
      autoplay: false,
      path: this.animationFile,
      rendererSettings: {preserveAspectRatio: 'xMidYMid meet'},
    });

    this.animation.addEventListener('data_failed', () => {
      this._handleAnimationError('scroll');
    });

    this.animation.addEventListener('DOMLoaded', () => {
      gsap.to(playhead, {
        frame: this.animation.totalFrames - 1,
        ease: 'none',
        onUpdate: () => this.animation.goToAndStop(playhead.frame, true),
        scrollTrigger: {
          trigger: this.element,
          start: EMBEDDED_SCROLL_START,
          end: endValue,
          scrub: scrubValue,
        },
      });
      ScrollTrigger.refresh();
    });
  }

  /**
   * Hover-triggered animation.
   * Plays once on first hover until the end, then stays on final frame.
   */
  _setupHoverAnimation() {
    const target = this.ui.$animationContainer;
    let hasPlayed = false;

    this.animation = Lottie.loadAnimation({
      container: target,
      renderer: 'svg',
      loop: false,
      autoplay: false,
      path: this.animationFile,
      rendererSettings: {preserveAspectRatio: 'xMidYMid meet'},
    });

    this.animation.addEventListener('data_failed', () => {
      this._handleAnimationError('hover');
    });

    this.element.addEventListener('mouseenter', () => {
      if (this.animation && !hasPlayed) {
        hasPlayed = true;
        this.animation.play();
      }
    });
  }

  /**
   * On-view triggered animation.
   * Plays when element becomes visible in viewport (configurable threshold).
   * Plays to completion and stops on final frame.
   * Optionally replays when element re-enters viewport.
   */
  _setupOnViewAnimation() {
    const target = this.ui.$animationContainer;
    const threshold = parseFloat(this.element.getAttribute('data-view-threshold')) || DEFAULT_VIEW_THRESHOLD;
    // Default to true (replay on view) unless explicitly set to 'false'
    const replayOnView = this.element.getAttribute('data-replay-on-view') !== 'false';
    let isPlaying = false;
    let hasCompleted = false;

    this.animation = Lottie.loadAnimation({
      container: target,
      renderer: 'svg',
      loop: false,
      autoplay: false,
      path: this.animationFile,
      rendererSettings: {preserveAspectRatio: 'xMidYMid meet'},
    });

    this.animation.addEventListener('data_failed', () => {
      this._handleAnimationError('on-view');
    });

    this.animation.addEventListener('complete', () => {
      isPlaying = false;
      hasCompleted = true;
    });

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !isPlaying) {
          // Element is in view and animation is not currently playing
          if (!hasCompleted || replayOnView) {
            // Reset to start if replaying
            if (hasCompleted && replayOnView) {
              this.animation.goToAndStop(0, true);
              hasCompleted = false;
            }
            isPlaying = true;
            this.animation.play();
          }
        }
      });
    }, {
      threshold: threshold,
    });

    this.animation.addEventListener('DOMLoaded', () => {
      observer.observe(this.element);
    });

    // Store observer reference for potential cleanup
    this._viewObserver = observer;
  }

  setVars() {
    // Get animation file from either data attribute (supports both naming conventions)
    this.animationFile = this.element.getAttribute('data-animation-file') ||
      this.element.getAttribute('data-desktop-animation-file');

    // Pinned scroll settings (for standalone sections only)
    this.animationDuration = parseFloat(this.element.getAttribute('data-animation-duration')) || DEFAULT_ANIMATION_DURATION;
    this.animationScrub = parseFloat(this.element.getAttribute('data-animation-scrub')) || DEFAULT_ANIMATION_SCRUB;
    this.showScrollHintFor = parseFloat(this.element.getAttribute('data-show-scroll-hint-for'));

    // Set min-height only for pinned scroll mode (standalone sections)
    if (this._getTriggerMode() === 'scroll-pinned') {
      this.element.style.minHeight = `${this.animationDuration}px`;
    }

    // Display conditions (for standalone sections)
    this.displayConditions = {
      displayOn: this.element.classList.contains('display-on'),
      mobile: this.element.classList.contains('display-on__Mobile'),
      tablet: this.element.classList.contains('display-on__Tablet'),
      desktop: this.element.classList.contains('display-on__Desktop'),
      portrait: this.element.classList.contains('display-on__Portrait'),
      landscape: this.element.classList.contains('display-on__Landscape'),
    };

    // Hash jump handling (for standalone sections)
    if (window.location.hash && document.getElementById(window.location.hash.slice(1)) && !['reload', 'back_forward'].includes(window.performance?.getEntries()[0]?.type)) {
      this.unresolvedJump = true;
      this.jumpTarget = document.getElementById(window.location.hash.slice(1));
    }
  }

  bindUi() {
    this.ui = {};
    // Support both container class names
    this.ui.$animationContainer = this.element.querySelector('.animationWindow') ||
      this.element.querySelector('.embeddable-animation__container');
  }
}

registerModule('Animation', Animation);
